import React from 'react';
import {CheckIcon} from '../Icons';
import type {BillingCycleName, SubscriptionPlan} from '../../domain/types';

interface PricingCardProps {
    name: string;
    price: number;
    features: string[];
    /** A standout bullet (e.g. the yearly-vs-monthly savings) rendered apart from the plain list. */
    savingsFeature?: string;
    billingCycle: BillingCycleName;
    isAuthenticated: boolean;
    highlight?: boolean;
    isCurrentPlan?: boolean;
    hasSubscription?: boolean;
    currentPlan?: SubscriptionPlan | null;
    loading?: boolean;
    onSubscribe: () => void;
}

const PricingCard: React.FC<PricingCardProps> = ({
  name,
  price,
  features,
  savingsFeature,
  billingCycle,
  isAuthenticated,
  highlight = false,
  isCurrentPlan = false,
  hasSubscription = false,
  currentPlan = null,
  loading = false,
  onSubscribe,
}) => {
  // A yearly subscriber looking at the monthly card is looking at a downgrade, which is not
  // offered — the card locks rather than pretending to be an upgrade path.
  const isDowngrade = !isCurrentPlan && currentPlan === 'pro_yearly' && billingCycle === 'monthly';
  const isLocked = isCurrentPlan || isDowngrade;

  const label = (() => {
    if (isCurrentPlan || isDowngrade) return 'Current Subscription';
    if (loading) return 'Loading...';
    // A signed-out visitor has no account yet: this is the trial signup, not an upgrade.
    if (!isAuthenticated) return 'Start your trial';
    if (hasSubscription) return 'Upgrade Subscription';
    return 'Subscribe Now';
  })();

  return (
    <div className={`relative flex flex-col rounded-lg p-6 sm:p-8 shadow-lg border-2 bg-gray-900 ${
      highlight ? 'border-primary-400' : 'border-primary-500'
    }`}>
      {isCurrentPlan && (
        <div className="absolute top-0 right-0 bg-green-500 text-white px-3 py-1 rounded-bl-lg text-xs font-semibold">
                    Current Plan
        </div>
      )}

      <h3 className="text-lg sm:text-xl font-semibold text-white">{name}</h3>
      <p className="mt-4 flex items-baseline">
        <span className="text-3xl sm:text-4xl font-bold text-white">${price}</span>
        <span className="ml-1 text-sm sm:text-base text-gray-400">
                    /{billingCycle === 'yearly' ? 'year' : 'month'}
        </span>
      </p>

      <ul className="mt-6 sm:mt-8 space-y-3 sm:space-y-4 flex-1">
        {features.map(feature => (
          <li key={feature} className="flex items-start gap-3">
            <CheckIcon className="h-5 w-5 shrink-0 text-primary-400" />
            <span className="text-sm sm:text-base text-gray-300">{feature}</span>
          </li>
        ))}

        {savingsFeature && (
          <li className="flex items-start gap-3">
            <CheckIcon className="h-5 w-5 shrink-0 text-emerald-400" />
            <span className="text-sm sm:text-base font-semibold text-emerald-400">
              {savingsFeature}
            </span>
          </li>
        )}
      </ul>

      <button
        onClick={onSubscribe}
        disabled={loading || isLocked}
        className={`mt-8 w-full rounded-md px-4 py-2 text-sm sm:text-base font-medium transition-colors ${
          isLocked
            ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
            : 'bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50'
        }`}
      >
        {label}
      </button>
    </div>
  );
};

export default PricingCard;
